import logger from 'utils/logger.util';

import fromAnyToInteger from '../fromAnyToInteger';

const L = logger('intl.util');

export function determineDayRangeUTCForTimezone(date, locale, timeZone) {
  if (!timeZone || !locale) {
    L.warn('determineDayRangeUTCForTimezone()', 'Invalid locale:', locale, 'or timeZone:', timeZone);
    return null;
  }

  const tz = timeZone.trim();

  // 1. Get the calendar date in the target timezone
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = fromAnyToInteger(parts.find((p) => p.type === 'year')?.value);
  const month = fromAnyToInteger(parts.find((p) => p.type === 'month')?.value);
  const day = fromAnyToInteger(parts.find((p) => p.type === 'day')?.value);

  // 2. Create UTC midnight for that calendar day
  const utcMidnight = new Date(Date.UTC(year, month - 1, day));

  // 3. Get timezone offset at that moment
  const offsetParts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(utcMidnight);

  const offset = offsetParts.find((p) => p.type === 'timeZoneName')?.value;
  const match = offset.match(/GMT([+-]\d{2})?(?::(\d{2}))?/);

  const offsetHours = fromAnyToInteger(match?.[1] ?? 0);
  const offsetMinutes = fromAnyToInteger(match?.[2] ?? 0);

  const offsetMs = (offsetHours * 60 + Math.sign(offsetHours) * offsetMinutes) * 60 * 1000;

  // 4. Apply offset → local midnight expressed in UTC
  const startUTC = new Date(utcMidnight.getTime() - offsetMs);
  const endUTC = new Date(startUTC.getTime() + 24 * 60 * 60 * 1000);

  return { startUTC, endUTC };
}

export function fromParamToDate(yyyymmdd) {
  if (!/^\d{8}$/.test(yyyymmdd)) {
    L.warn('fromParamToDate()', 'Invalid date format. Expected YYYYMMDD');
    return null;
  }

  const year = fromAnyToInteger(yyyymmdd.slice(0, 4));
  const month = fromAnyToInteger(yyyymmdd.slice(4, 6)) - 1; // 0-based
  const day = fromAnyToInteger(yyyymmdd.slice(6, 8));

  // Create a stable reference moment (UTC noon)
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
}

export function extractDateFromParam(nwyyyy) {
  const match = nwyyyy.match(/^(\d{1,2})W(\d{4})$/);
  if (!match) {
    L.warn('extractDateFromParam()', 'Invalid format. Expected NWYYYY (e.g. 2W2026)');
    return null;
  }

  return {
    week: fromAnyToInteger(match[1]),
    year: fromAnyToInteger(match[2]),
  };
}

export function determineISOMondayOfWeek(week, year) {
  if (!fromAnyToInteger(week) || !fromAnyToInteger(year)) {
    L.warn('determineISOMondayOfWeek()', 'Invalid parameters. `week` or `year` is not a number.');
    return;
  }
  // Jan 4th is always in ISO week 1
  const jan4 = new Date(Date.UTC(year, 0, 4, 12)); // 👈 UTC NOON

  const jan4Day = jan4.getUTCDay() || 7; // Sunday = 7
  const mondayOfWeek1 = new Date(jan4.getTime() - (jan4Day - 1) * 24 * 60 * 60 * 1000);

  // Anchor result at UTC noon as well
  return new Date(mondayOfWeek1.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
}

export function determineWeekRangeUTCForTimezone(nwyyyy, locale, timeZone) {
  const { week, year } = extractDateFromParam(nwyyyy);

  // Monday of the ISO week (UTC reference)
  const monday = determineISOMondayOfWeek(week, year);

  // Start of Monday (timezone → UTC)
  const { startUTC } = determineDayRangeUTCForTimezone(monday, locale, timeZone);

  // End of Sunday = next Monday midnight
  const endUTC = new Date(startUTC.getTime() + 7 * 24 * 60 * 60 * 1000);

  return { startUTC, endUTC };
}
